import { elementSymbol, implicitHydrogenLabel, isotopeSuperscript } from './elements.ts';
import {
  LABEL_FONT_ASCENDER,
  LABEL_FONT_DESCENDER,
  LABEL_GLYPH_EM,
  LABEL_GLYPHS,
  type LabelGlyph
} from './labelGlyphs.ts';

// #965 -- bonds stop a fixed distance short of the real letter shapes of an atom label, instead of
// at a rectangle around it (which had to be painted over the bond end with an opaque box, visible
// on any coloured background). The shapes are the convex hulls of Arimo Bold's outlines
// (labelGlyphs.ts), placed exactly as the label is drawn: centred on the atom, vertically centred
// like dominant-baseline="central" (textOutlines.ts outlineLabel), at the label's font size.

export interface OutlinePoint {
  readonly x: number;
  readonly y: number;
}

export type LabelHull = readonly OutlinePoint[];

// The visible gap between a letter and the end of a bond, at the standard 17px label size.
export const LABEL_BOND_GAP_PX = 1.75;

export const LABEL_FONT_SIZE_PX = 17;

// A character the table lacks (a free-text Markush label, say) is treated as a capital-sized block.
const FALLBACK_GLYPH: LabelGlyph = {
  advance: 722,
  hull: [
    [40, -716],
    [682, -716],
    [682, 0],
    [40, 0]
  ]
};

// The hull of every glyph of `text`, centred on x and vertically centred on y.
export function labelTextHulls(text: string, fontSizePx: number, x: number, y: number): LabelHull[] {
  const scale = fontSizePx / LABEL_GLYPH_EM;
  const glyphs = [...text].map((character) =>
    /\s/.test(character) ? null : (LABEL_GLYPHS[character] ?? FALLBACK_GLYPH)
  );
  const width = [...text].reduce((sum, character, index) => sum + (glyphs[index]?.advance ?? 278), 0) * scale;
  const baseline = y + ((LABEL_FONT_ASCENDER + LABEL_FONT_DESCENDER) / 2) * scale;
  let left = x - width / 2;
  const hulls: LabelHull[] = [];
  for (const glyph of glyphs) {
    if (glyph) hulls.push(glyph.hull.map(([gx, gy]) => ({ x: left + gx * scale, y: baseline + gy * scale })));
    left += (glyph?.advance ?? 278) * scale;
  }
  return hulls;
}

// An atom's whole label as drawn: its symbol (or override text) and, when shown, its H/H₂/H₃.
export function atomLabelHulls(
  atom: {
    readonly atomicNumber: number;
    readonly isotope?: number;
    readonly labelOverride?: string;
    readonly implicitHydrogenCount: number;
  },
  x: number,
  y: number,
  hydrogen: OutlinePoint | undefined,
  fontSizePx: number
): LabelHull[] {
  const symbol = atom.labelOverride ?? isotopeSuperscript(atom.isotope) + elementSymbol(atom.atomicNumber);
  const hulls = labelTextHulls(symbol, fontSizePx, x, y);
  if (hydrogen)
    hulls.push(
      ...labelTextHulls(implicitHydrogenLabel(atom.implicitHydrogenCount), fontSizePx, hydrogen.x, hydrogen.y)
    );
  return hulls;
}

function insideHull(point: OutlinePoint, hull: LabelHull): boolean {
  let sign = 0;
  for (let index = 0; index < hull.length; index++) {
    const a = hull[index]!;
    const b = hull[(index + 1) % hull.length]!;
    const cross = (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x);
    if (cross === 0) continue;
    if (sign === 0) sign = Math.sign(cross);
    else if (Math.sign(cross) !== sign) return false;
  }
  return true;
}

function distanceToSegment(point: OutlinePoint, a: OutlinePoint, b: OutlinePoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  const t =
    lengthSquared === 0 ? 0 : Math.min(1, Math.max(0, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared));
  return Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy));
}

// Distance from a point to a filled convex hull: zero inside it.
export function distanceToHull(point: OutlinePoint, hull: LabelHull): number {
  if (hull.length === 0) return Number.POSITIVE_INFINITY;
  if (hull.length > 2 && insideHull(point, hull)) return 0;
  let distance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < hull.length; index++) {
    distance = Math.min(distance, distanceToSegment(point, hull[index]!, hull[(index + 1) % hull.length]!));
  }
  return distance;
}

const SEARCH_STEPS = 40;

// Moving from `from` (an atom centre, or a line end near it) towards `toward`, the point where the
// line has cleared every hull by `clearance`. The distance to one convex hull is convex along the
// line, so the stretch within `clearance` of it is a single interval: find its nearest point, then
// bisect for where the interval ends; the farthest such end over all hulls wins. Returns `from`
// when no hull is within reach, and also when the cleared point would lie at or past `toward`
// (labels overlapping on a very short bond) -- the caller then keeps the untrimmed line.
export function clipToLabelOutline(
  from: OutlinePoint,
  toward: OutlinePoint,
  hulls: readonly LabelHull[],
  clearance: number
): OutlinePoint {
  const dx = toward.x - from.x;
  const dy = toward.y - from.y;
  if ((dx === 0 && dy === 0) || hulls.length === 0) return from;
  const at = (t: number) => ({ x: from.x + dx * t, y: from.y + dy * t });
  let exit = 0;
  for (const hull of hulls) {
    const distance = (t: number) => distanceToHull(at(t), hull);
    let low = 0;
    let high = 1;
    for (let step = 0; step < SEARCH_STEPS; step++) {
      const a = low + (high - low) / 3;
      const b = high - (high - low) / 3;
      if (distance(a) <= distance(b)) high = b;
      else low = a;
    }
    let inside = (low + high) / 2;
    if (distance(0) <= distance(inside)) inside = 0;
    if (distance(inside) >= clearance) continue;
    if (distance(1) < clearance) return from;
    let outside = 1;
    for (let step = 0; step < SEARCH_STEPS; step++) {
      const middle = (inside + outside) / 2;
      if (distance(middle) < clearance) inside = middle;
      else outside = middle;
    }
    exit = Math.max(exit, outside);
  }
  return exit === 0 ? from : at(exit);
}

// The text's advance width and its ink's reach above and below the label's centre line, in ems.
function textMetrics(text: string) {
  const glyphs = [...text].map((character) =>
    /\s/.test(character) ? null : (LABEL_GLYPHS[character] ?? FALLBACK_GLYPH)
  );
  const advance = glyphs.reduce((sum, glyph) => sum + (glyph?.advance ?? 278), 0) / LABEL_GLYPH_EM;
  const baseline = (LABEL_FONT_ASCENDER + LABEL_FONT_DESCENDER) / 2;
  const ys = glyphs.flatMap((glyph) => glyph?.hull.map(([, y]) => baseline + y) ?? []);
  return {
    advance,
    above: ys.length > 0 ? -Math.min(...ys) / LABEL_GLYPH_EM : 0,
    below: ys.length > 0 ? Math.max(...ys) / LABEL_GLYPH_EM : 0
  };
}

// Above or below, the ink of the symbol and its H keep this gap (in ems) -- about what sits
// between the letters of "NH" typed as one word.
const STACKED_HYDROGEN_GAP_EM = 0.15;

// #977 -- how far from the atom centre its H/H₂/H₃ label is centred, along `direction` (a unit
// vector), at `fontSizePx`. Beside the symbol the two texts sit as if typed as one word ("NH",
// "HO"): their advance widths touch. Above or below, their ink keeps a small gap. For a dragged,
// slanted direction, whichever of the two the label reaches first.
export function implicitHydrogenDistance(
  atom: {
    readonly atomicNumber: number;
    readonly isotope?: number;
    readonly labelOverride?: string;
    readonly implicitHydrogenCount: number;
  },
  direction: OutlinePoint,
  fontSizePx: number
): number {
  const symbol = textMetrics(atom.labelOverride ?? isotopeSuperscript(atom.isotope) + elementSymbol(atom.atomicNumber));
  const hydrogen = textMetrics(implicitHydrogenLabel(atom.implicitHydrogenCount));
  const across = (symbol.advance + hydrogen.advance) / 2;
  const stacked =
    direction.y < 0
      ? symbol.above + hydrogen.below + STACKED_HYDROGEN_GAP_EM
      : symbol.below + hydrogen.above + STACKED_HYDROGEN_GAP_EM;
  const alongX = Math.abs(direction.x) > 1e-9 ? across / Math.abs(direction.x) : Number.POSITIVE_INFINITY;
  const alongY = Math.abs(direction.y) > 1e-9 ? stacked / Math.abs(direction.y) : Number.POSITIVE_INFINITY;
  const distance = Math.min(alongX, alongY);
  return (Number.isFinite(distance) ? distance : across) * fontSizePx;
}

// Where an atom's H label is centred.
export function implicitHydrogenPosition(
  atom: Parameters<typeof implicitHydrogenDistance>[0],
  x: number,
  y: number,
  direction: OutlinePoint,
  fontSizePx: number
): OutlinePoint {
  const distance = implicitHydrogenDistance(atom, direction, fontSizePx);
  return { x: x + direction.x * distance, y: y + direction.y * distance };
}
